document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const $ = (selector, context = document) => context.querySelector(selector);
const $$ = (selector, context = document) => Array.from(context.querySelectorAll(selector));
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function showToast(message) {
  const toast = $('[data-toast]');
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add('is-visible');
  clearTimeout(showToast.timer);
  showToast.timer = setTimeout(() => toast.classList.remove('is-visible'), 2600);
}

function setBodyLock(lock) {
  document.body.classList.toggle('modal-open', Boolean(lock));
}

function initHeader() {
  const header = $('[data-site-header]');
  if (!header || header.dataset.initialized) return;
  header.dataset.initialized = 'true';

  const onScroll = () =>
    header.classList.toggle('is-scrolled', window.scrollY > 24);

  onScroll();

  window.addEventListener('scroll', onScroll, {
    passive: true
  });

  const drawer = $('#MobileDrawer');

  const openDrawer = () => {
    if (!drawer) return;

    drawer.setAttribute('open', '');
    drawer.setAttribute('aria-hidden', 'false');

    document.body.classList.add('menu-open');
  };

  const closeDrawer = () => {
    if (!drawer) return;

    drawer.removeAttribute('open');
    drawer.setAttribute('aria-hidden', 'true');

    document.body.classList.remove('menu-open');
  };

  $$('[data-menu-toggle]').forEach((button) =>
    button.addEventListener('click', openDrawer)
  );

  if (drawer) {
    drawer.addEventListener('click', (event) => {
      if (
        event.target === drawer ||
        event.target.closest('[data-drawer-close]')
      ) {
        closeDrawer();
      }
    });
  }

  const search = $('[data-search-overlay]');
  const searchInput = $('[data-search-input]');

  const openSearch = () => {
    if (!search) return;

    closeDrawer();

    search.setAttribute('open', '');
    search.setAttribute('aria-hidden', 'false');

    setBodyLock(true);

    window.setTimeout(() => searchInput?.focus(), 220);
  };

  const closeSearch = () => {
    if (!search) return;

    search.removeAttribute('open');
    search.setAttribute('aria-hidden', 'true');

    setBodyLock(false);
  };

  $$('[data-search-open]').forEach((button) =>
    button.addEventListener('click', openSearch)
  );

  $$('[data-search-close]').forEach((button) =>
    button.addEventListener('click', closeSearch)
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    closeDrawer();
    closeSearch();
    closeAllModals();
  });
}

function initReveal() {
  const items = $$('[data-reveal]:not([data-reveal-ready])');

  if (!items.length) return;

  items.forEach((item) =>
    item.setAttribute('data-reveal-ready', 'true')
  );

  if (
    reduceMotion ||
    window.ZIITOP?.motion === false ||
    !('IntersectionObserver' in window)
  ) {
    items.forEach((item) =>
      item.classList.add('is-visible')
    );

    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        entry.target.classList.add('is-visible');

        obs.unobserve(entry.target);
      });
    },
    {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.08
    }
  );

  items.forEach((item) =>
    observer.observe(item)
  );
}

function initHeroMotion() {
  const hero = $('[data-hero]');

  const media = hero
    ? $('[data-hero-media]', hero)
    : null;

  if (
    !hero ||
    !media ||
    hero.dataset.motionReady ||
    reduceMotion ||
    window.ZIITOP?.motion === false ||
    window.innerWidth < 900
  ) {
    return;
  }

  hero.dataset.motionReady = 'true';

  let scheduled = false;

  const update = () => {
    scheduled = false;

    const rect = hero.getBoundingClientRect();

    if (
      rect.bottom <= 0 ||
      rect.top >= window.innerHeight
    ) {
      return;
    }

    const progress = Math.max(
      0,
      Math.min(
        1,
        -rect.top / Math.max(rect.height, 1)
      )
    );

    media.style.transform =
      `translate3d(0, ${progress * 14}px, 0) scale(1.01)`;
  };

  const requestUpdate = () => {
    if (scheduled) return;

    scheduled = true;

    requestAnimationFrame(update);
  };

  update();

  window.addEventListener(
    'scroll',
    requestUpdate,
    {
      passive: true
    }
  );
}

async function refreshCartCount() {
  try {
    const response = await fetch(
      window.ZIITOP?.routes?.cartJson || '/cart.js',
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) return;

    const cart = await response.json();

    $$('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
    });
  } catch (_) {}
}

function initQuickAdd() {
  $$('.quick-add-form:not([data-quick-add-ready])').forEach((form) => {
    form.dataset.quickAddReady = 'true';

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const button = $('button[type="submit"]', form);
      const original = button?.innerHTML;

      if (button) {
        button.disabled = true;
        button.textContent = 'ADDING…';
      }

      try {
        const response = await fetch(
          window.ZIITOP?.routes?.cartAdd || '/cart/add',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json'
            },
            body: new FormData(form)
          }
        );

        if (!response.ok) {
          throw new Error('Could not add product');
        }

        await refreshCartCount();

        showToast('Added to cart');
      } catch (error) {
        showToast('Please choose options on the product page');
      } finally {
        if (button) {
          button.disabled = false;
          button.innerHTML = original;
        }
      }
    });
  });
}

function formatMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(
      document.documentElement.lang || 'en-US',
      {
        style: 'currency',
        currency:
          currency ||
          window.Shopify?.currency?.active ||
          'USD'
      }
    ).format(cents / 100);
  } catch (_) {
    return (cents / 100).toFixed(2);
  }
}

function initProduct(root) {
  if (!root || root.dataset.productReady) return;

  root.dataset.productReady = 'true';

  const jsonEl = $('[data-product-json]', root);

  if (!jsonEl) return;

  let product;

  try {
    product = JSON.parse(jsonEl.textContent);
  } catch (_) {
    return;
  }

  const optionGroups =
    $$('[data-option-group]', root);

  const variantSelect =
    $('[data-variant-select]', root);

  const price =
    $('[data-product-price]', root);

  const compare =
    $('[data-product-compare]', root);

  const sale =
    $('[data-product-sale]', root);

  const submit =
    $('[data-add-to-cart]', root);

  const submitLabel =
    $('[data-add-label]', root);

  const mobileButton =
    $('[data-mobile-atc-button]', root);

  const mobilePrice =
    $('[data-mobile-price]', root);

  const productForm =
    $('.product-form', root);

  const stockNote =
    $('[data-stock-note]', root);

  const stockText =
    $('[data-stock-text]', root);

  /*
    =====================================================
    PRODUCT GALLERY
    =====================================================
  */

  const slides =
    $$('[data-gallery-slide]', root);

  const thumbs =
    $$('[data-gallery-thumb]', root);

  const thumbList =
    $('[data-gallery-thumbs]', root);

  const galleryPrev =
    $('[data-gallery-prev]', root);

  const galleryNext =
    $('[data-gallery-next]', root);

  /*
    自动找到 Color 选项。
  */

  const colorGroup =
    optionGroups.find((group) =>
      Boolean(
        $('.pdp-option__values--color', group)
      )
    );

  const colorOptionIndex =
    colorGroup
      ? Number(colorGroup.dataset.optionIndex)
      : -1;

  const labels = {
    add:
      submit?.dataset.label ||
      'ADD TO CART',

    soldOut:
      'SOLD OUT',

    unavailable:
      'UNAVAILABLE',

    inStock:
      'IN STOCK'
  };

  let activeMediaPosition =
    Math.max(
      0,
      slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      )
    );

  let activeColorValue = null;

  let visibleMediaIds =
    new Set(
      slides.map((slide) =>
        String(slide.dataset.mediaId)
      )
    );

  const selectedOptions = () =>
    optionGroups.map(
      (group) =>
        $(
          '[data-option-input]:checked',
          group
        )?.value || ''
    );

  /*
    ============================================================
    COLOR → MEDIA GROUP
    ============================================================

    你的后台图片规则：

    Black+grey
      Black_grey_1
      Black_grey_2
      Black_grey_3
      ...

    Pink-3
      Pink-3_1
      Pink-3_2
      Pink-3_3
      ...

    Black+blue-1
      Black_blue-1_1
      Black_blue-1_2
      Black_blue-1_3
      ...

    这里不再使用 Variant Featured Media ID
    来推断一整个颜色的图片。

    而是：

    当前 Color 名称
        ↓
    标准化
        ↓
    图片文件名称标准化
        ↓
    找出所有属于这一 Color 的主图 + 辅图
  */

  const normalizeColorKey = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(
        /[^a-z0-9]+/g,
        ''
      );

  /*
    从 Shopify CDN URL
    提取原始文件名。
  */

  const getFileNameFromUrl = (src) => {
    if (!src) return '';

    try {
      const url =
        new URL(
          src,
          window.location.origin
        );

      return decodeURIComponent(
        url.pathname
          .split('/')
          .pop() || ''
      );
    } catch (_) {
      return String(src)
        .split('?')[0]
        .split('#')[0]
        .split('/')
        .pop() || '';
    }
  };

  /*
    从图片文件名得到颜色组 Key。

    例：

    Black_grey_1_ab9381e9-b147-410e-a9fc-558e9315ab30.jpg

    ↓

    Black_grey_1

    ↓

    Black_grey

    ↓

    blackgrey


    Pink-3_1_xxxxx.jpg

    ↓

    Pink-3

    ↓

    pink3
  */

  const getMediaGroupKeyFromUrl = (src) => {
    let stem =
      getFileNameFromUrl(src)
        .replace(
          /\.[a-z0-9]+$/i,
          ''
        );

    if (!stem) return '';

    /*
      去掉 Shopify / 导入工具生成的 UUID。

      例如：

      _ab9381e9-b147-410e-a9fc-558e9315ab30
    */

    stem =
      stem.replace(
        /[_-][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
        ''
      );

    /*
      只删除最后一个“下划线图片编号”。

      Pink-3_1
      ↓
      Pink-3

      Black_blue-1_6
      ↓
      Black_blue-1

      Black_8
      ↓
      Black

      特别注意：

      这里不能使用：

      /[_-]\d+$/

      否则：

      Pink-3
      Black-7
      Grey-1

      会被错误破坏。
    */

    stem =
      stem.replace(
        /_\d+$/i,
        ''
      );

    return normalizeColorKey(
      stem
    );
  };

  /*
    获取 Gallery Slide 的实际图片 URL。
  */

  const getSlideMediaUrl =
    (slide) => {
      if (!slide) return '';

      const zoomButton =
        $(
          '[data-media-zoom]',
          slide
        );

      const image =
        $('img', slide);

      return (
        zoomButton
          ?.dataset
          .zoomSrc
        ||
        image
          ?.currentSrc
        ||
        image
          ?.src
        ||
        ''
      );
    };

  /*
    一次性计算每张商品图属于哪个颜色组。
  */

  const slideMediaMeta =
    slides.map(
      (
        slide,
        index
      ) => ({
        index,

        mediaId:
          String(
            slide.dataset.mediaId ||
            ''
          ),

        groupKey:
          getMediaGroupKeyFromUrl(
            getSlideMediaUrl(
              slide
            )
          )
      })
    );

  /*
    =====================================================
    构建 Color → Media IDs
    =====================================================
  */

  const buildColorMediaGroups =
    () => {
      const groups =
        new Map();

      if (
        colorOptionIndex < 0 ||
        !colorGroup ||
        !slides.length
      ) {
        return groups;
      }

      const colorInputs =
        $$(
          '[data-option-input]',
          colorGroup
        );

      colorInputs.forEach(
        (input) => {
          const colorValue =
            input.value;

          const wantedKey =
            normalizeColorKey(
              colorValue
            );

          if (!wantedKey) return;

          /*
            找出这一颜色的全部图片。

            例如：

            Pink-3

            会匹配：

            Pink-3_1
            Pink-3_2
            Pink-3_3
            Pink-3_4
            ...
          */

          const mediaIds =
            slideMediaMeta

              .filter(
                (item) =>
                  item.groupKey ===
                    wantedKey &&
                  item.mediaId
              )

              .map(
                (item) =>
                  item.mediaId
              );

          if (
            mediaIds.length
          ) {
            groups.set(
              colorValue,
              mediaIds
            );
          }
        }
      );

      return groups;
    };

  const colorMediaGroups =
    buildColorMediaGroups();

  /*
    =====================================================
    FALLBACK：显示全部图片
    =====================================================
  */

  const showAllGalleryMedia =
    () => {
      visibleMediaIds =
        new Set(
          slides.map(
            (slide) =>
              String(
                slide.dataset.mediaId
              )
          )
        );

      thumbs.forEach(
        (thumb) => {
          thumb.hidden =
            false;

          thumb.setAttribute(
            'aria-hidden',
            'false'
          );
        }
      );
    };

  /*
    当前可见 Media IDs。
  */

  const getVisibleMediaIds =
    () =>
      slides

        .map(
          (slide) =>
            String(
              slide.dataset.mediaId
            )
        )

        .filter(
          (mediaId) =>
            visibleMediaIds.has(
              mediaId
            )
        );

  /*
    =====================================================
    更新左右箭头
    =====================================================
  */

  const updateGalleryControls =
    () => {
      const count =
        getVisibleMediaIds()
          .length;

      if (galleryPrev) {
        galleryPrev.hidden =
          count <= 1;
      }

      if (galleryNext) {
        galleryNext.hidden =
          count <= 1;
      }
    };

  /*
    =====================================================
    显示指定图片
    =====================================================
  */

  const setActiveMedia =
    (mediaId) => {
      if (!slides.length) return;

      const availableMediaIds =
        getVisibleMediaIds();

      let targetId =
        mediaId == null
          ? ''
          : String(mediaId);

      /*
        如果指定图片不属于当前颜色，
        自动回到当前颜色第一张。
      */

      if (
        !visibleMediaIds.has(
          targetId
        )
      ) {
        targetId =
          availableMediaIds[0]
          ||
          String(
            slides[0]
              ?.dataset
              .mediaId ||
            ''
          );
      }

      let position =
        slides.findIndex(
          (slide) =>
            String(
              slide.dataset.mediaId
            ) ===
            targetId
        );

      if (position < 0) {
        position = 0;
      }

      activeMediaPosition =
        position;

      /*
        主展示图。
      */

      slides.forEach(
        (
          slide,
          index
        ) => {
          const active =
            index === position;

          slide.classList.toggle(
            'is-active',
            active
          );

          slide.hidden =
            !active;

          slide.setAttribute(
            'aria-hidden',
            active
              ? 'false'
              : 'true'
          );

          if (!active) {
            $$(
              'video',
              slide
            ).forEach(
              (video) => {
                try {
                  video.pause();
                } catch (_) {}
              }
            );
          }
        }
      );

      const activeMediaId =
        String(
          slides[position]
            ?.dataset
            .mediaId ||
          ''
        );

      /*
        Thumbnail Active 状态。
      */

      thumbs.forEach(
        (thumb) => {
          const active =
            String(
              thumb.dataset.mediaId
            ) ===
            activeMediaId;

          thumb.classList.toggle(
            'is-active',
            active
          );

          thumb.setAttribute(
            'aria-current',
            active
              ? 'true'
              : 'false'
          );

          if (
            active &&
            !thumb.hidden
          ) {
            thumb.scrollIntoView({
              behavior:
                reduceMotion
                  ? 'auto'
                  : 'smooth',

              block:
                'nearest',

              inline:
                'nearest'
            });
          }
        }
      );
    };

  /*
    =====================================================
    根据 Color 切换整套主图 + 辅图
    =====================================================
  */

  const applyColorMediaGroup =
    (
      colorValue,
      fallbackMediaId = null
    ) => {

      /*
        商品没有 Color，
        或无法建立 Color Groups。

        保持主题原始逻辑。
      */

      if (
        colorOptionIndex < 0 ||
        !colorValue ||
        !colorMediaGroups.size
      ) {
        showAllGalleryMedia();

        updateGalleryControls();

        if (
          fallbackMediaId
        ) {
          setActiveMedia(
            fallbackMediaId
          );
        } else if (
          slides.length
        ) {
          setActiveMedia(
            slides[
              activeMediaPosition
            ]?.dataset.mediaId
            ||
            slides[0]
              ?.dataset
              .mediaId
          );
        }

        return;
      }

      const groupMediaIds =
        colorMediaGroups.get(
          colorValue
        );

      /*
        当前 Color 文件名匹配失败时：

        不隐藏成空白。

        显示全部图片作为 fallback，
        并优先跳 Variant 主图。
      */

      if (
        !groupMediaIds
          ?.length
      ) {
        showAllGalleryMedia();

        updateGalleryControls();

        activeColorValue =
          colorValue;

        if (
          fallbackMediaId
        ) {
          setActiveMedia(
            fallbackMediaId
          );
        }

        return;
      }

      const colorChanged =
        colorValue !==
        activeColorValue;

      const currentMediaId =
        String(
          slides[
            activeMediaPosition
          ]?.dataset.mediaId ||
          ''
        );

      /*
        当前颜色允许显示的全部图片。
      */

      visibleMediaIds =
        new Set(
          groupMediaIds.map(
            String
          )
        );

      /*
        只保留当前颜色 Thumbnail。
      */

      thumbs.forEach(
        (thumb) => {
          const belongsToCurrentColor =
            visibleMediaIds.has(
              String(
                thumb.dataset.mediaId
              )
            );

          thumb.hidden =
            !belongsToCurrentColor;

          thumb.setAttribute(
            'aria-hidden',
            belongsToCurrentColor
              ? 'false'
              : 'true'
          );
        }
      );

      updateGalleryControls();

      activeColorValue =
        colorValue;

      /*
        换颜色：

        → 回当前颜色第一张，也就是主图。

        只换 Size：

        → 保持当前正在看的辅图。
      */

      if (
        colorChanged ||
        !visibleMediaIds.has(
          currentMediaId
        )
      ) {
        setActiveMedia(
          groupMediaIds[0]
        );
      } else {
        setActiveMedia(
          currentMediaId
        );
      }
    };

  /*
    =====================================================
    Thumbnail Click
    =====================================================
  */

  thumbs.forEach(
    (thumb) => {
      thumb.addEventListener(
        'click',
        () => {
          if (thumb.hidden) {
            return;
          }

          setActiveMedia(
            thumb.dataset.mediaId
          );
        }
      );
    }
  );

  /*
    =====================================================
    Thumbnail Keyboard
    =====================================================
  */

  thumbList
    ?.addEventListener(
      'keydown',
      (event) => {
        if (
          ![
            'ArrowRight',
            'ArrowLeft',
            'Home',
            'End'
          ].includes(
            event.key
          )
        ) {
          return;
        }

        const visibleThumbs =
          thumbs.filter(
            (thumb) =>
              !thumb.hidden
          );

        if (
          !visibleThumbs.length
        ) {
          return;
        }

        event.preventDefault();

        const focusedIndex =
          Math.max(
            0,
            visibleThumbs.indexOf(
              document.activeElement
            )
          );

        let next =
          focusedIndex;

        if (
          event.key ===
          'Home'
        ) {
          next = 0;

        } else if (
          event.key ===
          'End'
        ) {
          next =
            visibleThumbs.length -
            1;

        } else if (
          event.key ===
          'ArrowRight'
        ) {
          next =
            (
              focusedIndex +
              1
            )
            %
            visibleThumbs.length;

        } else {
          next =
            (
              focusedIndex -
              1 +
              visibleThumbs.length
            )
            %
            visibleThumbs.length;
        }

        visibleThumbs[
          next
        ].focus();

        visibleThumbs[
          next
        ].click();
      }
    );

  /*
    =====================================================
    PREVIOUS
    =====================================================
  */

  galleryPrev
    ?.addEventListener(
      'click',
      () => {
        const mediaIds =
          getVisibleMediaIds();

        if (
          !mediaIds.length
        ) {
          return;
        }

        const currentId =
          String(
            slides[
              activeMediaPosition
            ]?.dataset.mediaId ||
            ''
          );

        const currentIndex =
          Math.max(
            0,
            mediaIds.indexOf(
              currentId
            )
          );

        const nextIndex =
          (
            currentIndex -
            1 +
            mediaIds.length
          )
          %
          mediaIds.length;

        setActiveMedia(
          mediaIds[
            nextIndex
          ]
        );
      }
    );

  /*
    =====================================================
    NEXT
    =====================================================
  */

  galleryNext
    ?.addEventListener(
      'click',
      () => {
        const mediaIds =
          getVisibleMediaIds();

        if (
          !mediaIds.length
        ) {
          return;
        }

        const currentId =
          String(
            slides[
              activeMediaPosition
            ]?.dataset.mediaId ||
            ''
          );

        const currentIndex =
          Math.max(
            0,
            mediaIds.indexOf(
              currentId
            )
          );

        const nextIndex =
          (
            currentIndex +
            1
          )
          %
          mediaIds.length;

        setActiveMedia(
          mediaIds[
            nextIndex
          ]
        );
      }
    );

  /*
    =====================================================
    Variant Availability
    =====================================================
  */

  const refreshOptionAvailability =
    () => {
      const selected =
        selectedOptions();

      optionGroups.forEach(
        (
          group,
          optionIndex
        ) => {
          $$(
            '[data-option-input]',
            group
          ).forEach(
            (input) => {
              const hasAvailableVariant =
                product.variants.some(
                  (variant) => {
                    if (
                      !variant.available ||
                      variant.options[
                        optionIndex
                      ] !==
                      input.value
                    ) {
                      return false;
                    }

                    return variant.options
                      .slice(
                        0,
                        optionIndex
                      )
                      .every(
                        (
                          option,
                          index
                        ) =>
                          option ===
                          selected[
                            index
                          ]
                      );
                  }
                );

              const chip =
                input.nextElementSibling;

              if (chip) {
                chip.classList.toggle(
                  'is-unavailable',
                  !hasAvailableVariant
                );
              }

              input.disabled =
                !hasAvailableVariant;

              input.setAttribute(
                'aria-disabled',
                hasAvailableVariant
                  ? 'false'
                  : 'true'
              );
            }
          );
        }
      );
    };

  /*
    =====================================================
    Purchase State
    =====================================================
  */

  const setPurchaseState =
    (variant) => {
      const available =
        Boolean(
          variant?.available
        );

      const hasVariant =
        Boolean(
          variant
        );

      const statusLabel =
        !hasVariant
          ? labels.unavailable
          : available
            ? labels.inStock
            : labels.soldOut;

      const buttonLabel =
        !hasVariant
          ? labels.unavailable
          : available
            ? labels.add
            : labels.soldOut;

      if (submit) {
        submit.disabled =
          !available;
      }

      if (submitLabel) {
        submitLabel.textContent =
          buttonLabel;
      }

      if (mobileButton) {
        mobileButton.disabled =
          !available;

        mobileButton.textContent =
          buttonLabel;
      }

      if (stockText) {
        stockText.textContent =
          statusLabel;
      }

      stockNote
        ?.classList
        .toggle(
          'is-unavailable',
          !available
        );
    };

  /*
    当前完整 Size + Color Variant
    不存在时，
    仍然找到当前 Color 的一个 Variant，
    仅用于 fallback Featured Media。
  */

  const findVariantForSelectedColor =
    (selected) => {
      if (
        colorOptionIndex < 0
      ) {
        return null;
      }

      const selectedColor =
        selected[
          colorOptionIndex
        ];

      if (
        !selectedColor
      ) {
        return null;
      }

      return (
        product.variants.find(
          (variant) =>
            variant.options?.[
              colorOptionIndex
            ] ===
              selectedColor
            &&
            variant
              .featured_media_id
        )
        ||
        product.variants.find(
          (variant) =>
            variant.options?.[
              colorOptionIndex
            ] ===
            selectedColor
        )
        ||
        null
      );
    };

  /*
    =====================================================
    Variant Update
    =====================================================
  */

  const updateVariant =
    () => {
      const selected =
        selectedOptions();

      /*
        Gallery 直接读取当前 Color。

        不再通过 Variant 主图
        判断是什么颜色。
      */

      const selectedColor =
        colorOptionIndex >= 0
          ? selected[
              colorOptionIndex
            ]
          : '';

      const variant =
        product.variants.find(
          (candidate) =>
            candidate.options.every(
              (
                option,
                index
              ) =>
                option ===
                selected[
                  index
                ]
            )
        );

      /*
        更新 Color / Size 标题文字。
      */

      optionGroups.forEach(
        (group) => {
          const label =
            $(
              '[data-option-label]',
              group
            );

          const checked =
            $(
              '[data-option-input]:checked',
              group
            );

          if (
            label &&
            checked
          ) {
            label.textContent =
              checked.value;
          }
        }
      );

      /*
        Gallery 永远先跟当前 Color 走。

        即使当前 Size 暂时没有对应 Variant，
        当前颜色图片也仍然正确显示。
      */

      const colorVariant =
        variant
        ||
        findVariantForSelectedColor(
          selected
        );

      applyColorMediaGroup(
        selectedColor,
        colorVariant
          ?.featured_media_id
        ||
        null
      );

      /*
        当前组合不存在。
      */

      if (!variant) {
        if (
          variantSelect
        ) {
          variantSelect.value =
            '';
        }

        setPurchaseState(
          null
        );

        refreshOptionAvailability();

        return;
      }

      /*
        更新 Variant Select。
      */

      if (
        variantSelect
      ) {
        variantSelect.value =
          String(
            variant.id
          );
      }

      /*
        Price。
      */

      const formatted =
        formatMoney(
          variant.price,
          product.currency
        );

      if (
        price
      ) {
        price.textContent =
          formatted;
      }

      if (
        mobilePrice
      ) {
        mobilePrice.textContent =
          formatted;
      }

      /*
        Compare Price / Sale。
      */

      if (
        compare
      ) {
        if (
          variant
            .compare_at_price
          &&
          variant
            .compare_at_price
            >
            variant.price
        ) {
          compare.textContent =
            formatMoney(
              variant
                .compare_at_price,
              product.currency
            );

          compare.hidden =
            false;

          if (
            sale
          ) {
            const percent =
              Math.round(
                (
                  (
                    variant
                      .compare_at_price
                    -
                    variant.price
                  )
                  /
                  variant
                    .compare_at_price
                )
                *
                100
              );

            sale.textContent =
              `SAVE ${percent}%`;

            sale.hidden =
              false;
          }
        } else {
          compare.hidden =
            true;

          if (
            sale
          ) {
            sale.hidden =
              true;
          }
        }
      }

      setPurchaseState(
        variant
      );

      /*
        URL 同步 Variant。
      */

      if (
        history.replaceState
      ) {
        const url =
          new URL(
            window.location.href
          );

        url.searchParams.set(
          'variant',
          variant.id
        );

        history.replaceState(
          {},
          '',
          url
        );
      }

      refreshOptionAvailability();
    };

  /*
    =====================================================
    Option Change
    =====================================================
  */

  $$(
    '[data-option-input]',
    root
  ).forEach(
    (input) =>
      input.addEventListener(
        'change',
        updateVariant
      )
  );

  /*
    =====================================================
    Native Variant Select
    =====================================================
  */

  variantSelect
    ?.addEventListener(
      'change',
      () => {
        const selectedVariant =
          product.variants.find(
            (variant) =>
              String(
                variant.id
              )
              ===
              String(
                variantSelect.value
              )
          );

        if (
          !selectedVariant
        ) {
          return;
        }

        optionGroups.forEach(
          (
            group,
            index
          ) => {
            const matching =
              $$(
                '[data-option-input]',
                group
              ).find(
                (input) =>
                  input.value ===
                  selectedVariant
                    .options[
                      index
                    ]
              );

            if (
              matching
            ) {
              matching.checked =
                true;
            }
          }
        );

        updateVariant();
      }
    );

  /*
    页面首次打开时执行。

    如果 URL 中带 ?variant=，
    Liquid 已经选中了对应 Option，
    这里会直接过滤到对应颜色主图 + 辅图。
  */

  updateVariant();

  /*
    =====================================================
    Quantity
    =====================================================
  */

  const qty =
    $(
      'input[name="quantity"]',
      productForm ||
      root
    );

  $('[data-qty-minus]', root)
    ?.addEventListener(
      'click',
      () => {
        if (!qty) return;

        qty.value =
          Math.max(
            Number(
              qty.min ||
              1
            ),

            Number(
              qty.value ||
              1
            )
            -
            1
          );
      }
    );

  $('[data-qty-plus]', root)
    ?.addEventListener(
      'click',
      () => {
        if (!qty) return;

        qty.value =
          Number(
            qty.value ||
            1
          )
          +
          1;
      }
    );

  /*
    =====================================================
    Mobile Add To Cart
    =====================================================
  */

  if (
    mobileButton &&
    productForm
  ) {
    mobileButton
      .addEventListener(
        'click',
        () =>
          productForm
            .requestSubmit()
      );
  }

  /*
    =====================================================
    Mobile Sticky ATC
    =====================================================
  */

  const sticky =
    $('[data-mobile-atc]', root);

  if (
    sticky &&
    productForm &&
    'IntersectionObserver' in
      window
  ) {
    const observer =
      new IntersectionObserver(
        ([entry]) =>
          sticky
            .classList
            .toggle(
              'is-visible',
              !entry.isIntersecting
            ),
        {
          threshold: 0.05
        }
      );

    observer.observe(
      productForm
    );
  }
}

let activeModal = null;
let modalReturnFocus = null;

function getFocusable(container) {
  return $$(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    container
  ).filter(
    (element) =>
      !element.hidden &&
      element.offsetParent !== null
  );
}

function openModal(
  modal,
  trigger = document.activeElement
) {
  if (!modal) return;

  activeModal = modal;

  modalReturnFocus =
    trigger instanceof HTMLElement
      ? trigger
      : null;

  modal.setAttribute(
    'open',
    ''
  );

  modal.setAttribute(
    'aria-hidden',
    'false'
  );

  setBodyLock(true);

  window.setTimeout(
    () => {
      const focusTarget =
        getFocusable(modal)[0]
        ||
        $('[role="dialog"]', modal)
        ||
        modal;

      focusTarget
        ?.focus();
    },
    0
  );
}

function closeModal(
  modal,
  restoreFocus = true
) {
  if (!modal) return;

  modal.removeAttribute(
    'open'
  );

  modal.setAttribute(
    'aria-hidden',
    'true'
  );

  if (
    activeModal === modal
  ) {
    activeModal =
      null;
  }

  setBodyLock(false);

  if (
    restoreFocus &&
    modalReturnFocus
  ) {
    modalReturnFocus
      .focus();
  }

  modalReturnFocus =
    null;
}

function closeAllModals() {
  $$(
    '[data-size-guide-modal][open], [data-media-modal][open]'
  ).forEach(
    (modal) =>
      closeModal(
        modal,
        false
      )
  );

  if (
    modalReturnFocus
  ) {
    modalReturnFocus
      .focus();
  }

  modalReturnFocus =
    null;
}

document.addEventListener(
  'keydown',
  (event) => {
    if (
      !activeModal ||
      event.key !== 'Tab'
    ) {
      return;
    }

    const focusable =
      getFocusable(
        activeModal
      );

    if (
      !focusable.length
    ) {
      event.preventDefault();

      return;
    }

    const first =
      focusable[0];

    const last =
      focusable[
        focusable.length -
        1
      ];

    if (
      event.shiftKey &&
      document.activeElement ===
        first
    ) {
      event.preventDefault();

      last.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement ===
        last
    ) {
      event.preventDefault();

      first.focus();
    }
  }
);

function initProductModals(root) {
  if (
    !root ||
    root.dataset.modalReady
  ) {
    return;
  }

  root.dataset.modalReady =
    'true';

  const sizeModal =
    $(
      '[data-size-guide-modal]',
      root
    );

  $$(
    '[data-size-guide-open]',
    root
  ).forEach(
    (button) =>
      button.addEventListener(
        'click',
        () =>
          openModal(
            sizeModal,
            button
          )
      )
  );

  $$(
    '[data-size-guide-close]',
    root
  ).forEach(
    (button) =>
      button.addEventListener(
        'click',
        () =>
          closeModal(
            sizeModal
          )
      )
  );

  const mediaModal =
    $(
      '[data-media-modal]',
      root
    );

  const mediaImage =
    $(
      '[data-media-modal-image]',
      root
    );

  $$(
    '[data-media-zoom]',
    root
  ).forEach(
    (button) =>
      button.addEventListener(
        'click',
        () => {
          if (
            mediaImage
          ) {
            mediaImage.src =
              button
                .dataset
                .zoomSrc
              ||
              '';
          }

          openModal(
            mediaModal,
            button
          );
        }
      )
  );

  $$(
    '[data-media-close]',
    root
  ).forEach(
    (button) =>
      button.addEventListener(
        'click',
        () =>
          closeModal(
            mediaModal
          )
      )
  );
}

function initUseTabs(
  context = document
) {
  $$(
    '[data-use-tabs]:not([data-use-tabs-ready])',
    context
  ).forEach(
    (root) => {
      root.dataset.useTabsReady =
        'true';

      const tabs =
        $$(
          '[data-use-tab]',
          root
        );

      const panels =
        $$(
          '[data-use-panel]',
          root
        );

      if (
        !tabs.length ||
        !panels.length
      ) {
        return;
      }

      const activate =
        (
          index,
          focus = false
        ) => {
          tabs.forEach(
            (
              tab,
              i
            ) => {
              const active =
                i === index;

              tab.classList.toggle(
                'is-active',
                active
              );

              tab.setAttribute(
                'aria-selected',
                active
                  ? 'true'
                  : 'false'
              );

              tab.tabIndex =
                active
                  ? 0
                  : -1;

              if (
                active &&
                focus
              ) {
                tab.focus();
              }
            }
          );

          panels.forEach(
            (
              panel,
              i
            ) => {
              const active =
                i === index;

              panel.classList.toggle(
                'is-active',
                active
              );

              panel.hidden =
                !active;
            }
          );
        };

      tabs.forEach(
        (
          tab,
          index
        ) => {
          tab.addEventListener(
            'click',
            () =>
              activate(
                index
              )
          );

          tab.addEventListener(
            'keydown',
            (event) => {
              if (
                ![
                  'ArrowRight',
                  'ArrowLeft',
                  'ArrowDown',
                  'ArrowUp',
                  'Home',
                  'End'
                ].includes(
                  event.key
                )
              ) {
                return;
              }

              event.preventDefault();

              let next =
                index;

              if (
                event.key ===
                'Home'
              ) {
                next = 0;

              } else if (
                event.key ===
                'End'
              ) {
                next =
                  tabs.length -
                  1;

              } else if (
                event.key ===
                  'ArrowRight'
                ||
                event.key ===
                  'ArrowDown'
              ) {
                next =
                  (
                    index +
                    1
                  )
                  %
                  tabs.length;

              } else {
                next =
                  (
                    index -
                    1 +
                    tabs.length
                  )
                  %
                  tabs.length;
              }

              activate(
                next,
                true
              );
            }
          );
        }
      );

      activate(0);
    }
  );
}

function initAccordions() {
  $$(
    '.accordion details:not([data-accordion-ready])'
  ).forEach(
    (detail) => {
      detail.dataset.accordionReady =
        'true';

      detail.addEventListener(
        'toggle',
        () => {
          if (
            !detail.open
          ) {
            return;
          }

          const container =
            detail.closest(
              '.accordion'
            );

          $$(
            'details[open]',
            container
          ).forEach(
            (other) => {
              if (
                other !==
                detail
              ) {
                other.removeAttribute(
                  'open'
                );
              }
            }
          );
        }
      );
    }
  );
}

function initAll(
  context = document
) {
  initHeader();

  initReveal();

  initHeroMotion();

  initQuickAdd();

  $$(
    '[data-product-root]',
    context
  ).forEach(
    (root) => {
      initProduct(
        root
      );

      initProductModals(
        root
      );
    }
  );

  initAccordions();

  initUseTabs(
    context
  );
}

if (
  document.readyState ===
  'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    () =>
      initAll()
  );
} else {
  initAll();
}

document.addEventListener(
  'shopify:section:load',
  (event) =>
    initAll(
      event.target
    )
);